import React from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useTranslation } from 'react-i18next';
import { showSizeName } from '../../utils/sellableSizes';

/**
 * Οι παραγγελίες μιας ΚΛΕΙΣΜΕΝΗΣ συνεδρίας τραπεζιού (modal ιστορικού της κονσόλας τραπεζιών).
 *
 * ΓΙΑΤΙ ξεχωριστό component (FIX-FE(c), FE-10b): το όνομα μεγέθους εδώ κρύβεται με τον ΙΔΙΟ
 * κανόνα (showSizeName) όπως στους ανοιχτούς λογαριασμούς της ίδιας οθόνης και στο app του
 * σερβιτόρου — απόφαση 15 για το μοναδικό πωλήσιμο μέγεθος, F1-W4-4 για τα τέλη. Αλλιώς η
 * κονσόλα έκρυβε το «Κανονικό» όσο το τραπέζι ήταν ανοιχτό και το ξανάδειχνε μόλις έκλεινε.
 * Ζει έξω από το DineInOperations ώστε να δοκιμάζεται χωρίς όλη την οθόνη.
 *
 * ΜΟΝΟ εμφάνιση: τα ποσά (total_price) δείχνονται αυτούσια όπως τα γύρισε ο server, και
 * τίποτα δεν ξαναγράφεται στο ιστορικό. Προϊόν που δεν υπάρχει πια στον κατάλογο = το όνομα
 * μεγέθους φαίνεται (σε αμφιβολία δεν κρύβουμε).
 */

export interface ClosedSessionItem {
  id: number | string;
  /**
   * ΥΠΟΧΡΕΩΤΙΚΟ: χωρίς αυτό το showSizeName δεν βρίσκει προϊόν και δείχνει ΠΑΝΤΑ το όνομα.
   * Το γυρίζει το GET /dine-in/:storeId/sessions/:sessionId/details από το FIX-FE(c)·
   * backend πριν από αυτό = undefined = το όνομα φαίνεται (ασφαλής πλευρά).
   */
  product_id: number | string;
  product_name: string;
  quantity: number | string;
  /** NULL όταν το μέγεθος σβήστηκε (FK SET NULL, 239) — τότε ταίριασμα με όνομα. */
  size_id: number | string | null;
  size_name?: string | null;
  total_price: number | string;
}

export interface ClosedSessionOrder {
  id: number | string;
  order_number: number | string;
  created_at: string;
  items?: ClosedSessionItem[] | null;
}

interface Props {
  orders: ClosedSessionOrder[];
  /** Ο κατάλογος που έχει ήδη φορτώσει η κονσόλα (μόνο πωλήσιμα μεγέθη). */
  products: Parameters<typeof showSizeName>[0];
}

const ClosedSessionOrders: React.FC<Props> = ({ orders, products }) => {
  const { t } = useTranslation(['dineIn']);
  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <div key={order.id} className="border border-line-strong rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-t1">{t('dineIn:orderNumber', { number: order.order_number })}</span>
            <span className="text-sm text-t3">
              {new Date(order.created_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {order.items && order.items.map((item) => (
            <div key={item.id} className="flex items-center justify-between py-1 text-sm text-t2">
              <span>
                {item.quantity}x {item.product_name}
                {item.size_name
                  && showSizeName(products, item.product_id, { id: item.size_id, name: item.size_name })
                  && <span className="text-t4"> ({item.size_name})</span>}
              </span>
              <span className="font-medium text-t2">{formatMoney(parseFloat(String(item.total_price)))}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ClosedSessionOrders;
